import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ResetPasswordForm } from '@/components/auth/ResetPasswordForm';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const authBgImage = '/bottle-mesh.jpg';

export default function ResetPassword() {
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hashError, setHashError] = useState<{ code: string; description: string } | null>(null);

  // Parse URL hash parameters for errors
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const error = params.get('error');
      const errorCode = params.get('error_code');
      const errorDescription = params.get('error_description');
      
      if (error && errorCode) {
        setHashError({
          code: errorCode,
          description: errorDescription || error,
        });
      }
    }
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = authBgImage;
    img.onload = () => {
      setImageLoaded(true);
    };
  }, []);

  // Check if user has a valid recovery session
  useEffect(() => {
    if (!isLoading && !session && !hashError) {
      // No session and no error means they didn't come from a valid reset link
      navigate('/auth');
    }
  }, [session, isLoading, navigate, hashError]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show error message if there's a hash error
  if (hashError) {
    const getErrorMessage = () => {
      switch (hashError.code) {
        case 'otp_expired':
          return {
            title: 'Reset Link Expired',
            description: 'Your password reset link has expired or was already used. This can happen if you clicked the link more than once, or if your email client or browser (especially Safari) previewed the link automatically.',
          };
        case 'otp_disabled':
          return {
            title: 'Password Reset Unavailable',
            description: 'Password reset links have been temporarily disabled. Please contact support for assistance.',
          };
        default:
          return {
            title: 'Invalid Reset Link',
            description: 'This password reset link is invalid or has already been used. Please request a new one.',
          };
      }
    };

    const errorInfo = getErrorMessage();

    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-background" style={{ backgroundImage: 'var(--bg-stripe-pattern)' }}>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{errorInfo.title}</CardTitle>
            <CardDescription>Request a new password reset link to continue</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>What happened?</AlertTitle>
              <AlertDescription className="mt-2">
                {errorInfo.description}
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                <strong>Safari users:</strong> Safari's link preview feature may consume password reset links before you can use them. To avoid this:
              </p>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 ml-2">
                <li>Disable link previews in Safari settings</li>
                <li>Copy the link and paste it into a private/incognito window</li>
                <li>Make sure you only click the reset link once</li>
              </ul>
            </div>

            <Button 
              onClick={() => navigate('/auth?forgot=true')} 
              className="w-full"
            >
              Request New Reset Link
            </Button>
            
            <Button 
              onClick={() => navigate('/auth')} 
              variant="outline"
              className="w-full"
            >
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-col justify-center items-center relative p-12 text-primary-foreground overflow-hidden">
        {/* Background image layer */}
        <div
          className={cn(
            "absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out",
            imageLoaded ? "opacity-100" : "opacity-0",
            !imageLoaded && "bg-primary/20"
          )}
          style={{ backgroundImage: `url(${authBgImage})` }}
        />
        {/* Stripe pattern layer */}
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-1000 ease-in-out"
          )}
          style={{ 
            backgroundImage: 'var(--bg-stripe-pattern)',
            opacity: imageLoaded ? 0.15 : 0
          }}
        />
        {/* Red overlay at 35% opacity with multiply blend mode */}
        <div 
          className={cn(
            "absolute inset-0 transition-all duration-1000 ease-in-out",
            imageLoaded ? "bg-[#CAC0B6]" : "bg-[#CAC0B6]"
          )}
          style={{ 
            opacity: imageLoaded ? 0.35 : 0.7,
            mixBlendMode: 'color'
          }} 
        />
        {/* Content on top */}
        <div className="relative z-10 max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <img src="/gravity-assist-white-logo.svg" alt="Gravity Assist" className="h-12" />
          </div>
          <p className="text-lg opacity-90">
            Streamline your winery operations with intelligent workflow management,
            intake processing, and comprehensive cellar operations tracking.
          </p>
        </div>
      </div>

      {/* Right side - Reset form */}
      <div className="flex items-center justify-center p-8 bg-background" style={{ backgroundImage: 'var(--bg-stripe-pattern)' }}>
        <div className="w-full max-w-md space-y-6">
          <div className="lg:hidden flex items-center gap-2 justify-center mb-8">
            <img src="/gravity-assist-logo.svg" alt="Gravity Assist" className="h-8" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Reset Your Password</CardTitle>
              <CardDescription>
                Enter your new password below
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResetPasswordForm />
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
